import {
  GenericDropdown,
  GenericDropdownContext,
  Icon,
  IconSVG,
  type ListItemProps,
  SVGColorKey,
} from "@studio3t/generic-components";
import type React from "react";
import { useMemo } from "react";
import type { ConversationMode } from "@/api/ai/types";
import { FilterTrigger } from "@/components/TableFilters";

interface ModeSelectorProps {
  mode: ConversationMode;
  onModeChange: (mode: ConversationMode) => void;
  /** When true, the `Act` option is rendered disabled. */
  actDisabled?: boolean;
}

const MODE_LABEL: Record<ConversationMode, string> = {
  act: "Act",
  ask: "Ask",
};

function buildDropdownItems(
  current: ConversationMode,
  onChange: (mode: ConversationMode) => void,
  actDisabled: boolean,
): ListItemProps[][] {
  const modes: ConversationMode[] = ["act", "ask"];
  return [
    modes.map((m) => {
      const disabled = m === "act" && actDisabled;
      return {
        id: m,
        label: MODE_LABEL[m],
        type: "text" as const,
        value: m,
        disabled,
        // The current mode is marked with the list's dot, as the Studio
        // helper's conversation list marks the open chat.
        selected: {
          value: m === current,
          icon: "dot" as const,
        },
        onClick: () => {
          if (disabled || m === current) return;
          onChange(m);
        },
      };
    }),
  ];
}

export const ModeSelector: React.FC<ModeSelectorProps> = ({
  mode,
  onModeChange,
  actDisabled = false,
}) => {
  const dropdownItems = useMemo(
    () => buildDropdownItems(mode, onModeChange, actDisabled),
    [mode, onModeChange, actDisabled],
  );

  // The Figma footer's Secondary small button with a chevron — the same
  // trigger the table filters use.
  return (
    <GenericDropdown
      dropdownItems={dropdownItems}
      width={{ strategy: "fixed", size: 120 }}
      placement="top-start"
    >
      <GenericDropdownContext.Consumer>
        {({ open }) => (
          <FilterTrigger type="button" $active={open} data-testid="agent-mode">
            {MODE_LABEL[mode]}
            <IconSVG
              icon={Icon.BaseChevron}
              size={10}
              svgColorKey={SVGColorKey.semanticTextBody}
            />
          </FilterTrigger>
        )}
      </GenericDropdownContext.Consumer>
    </GenericDropdown>
  );
};

export default ModeSelector;
